import React from 'react';
import { Bell, X } from 'lucide-react';
import { Button } from '../ui/button';

/**
 * Notification Permission Modal
 * 
 * Shows after onboarding to request push notification permissions.
 * Designed to be friendly and explain the benefits of enabling notifications.
 */
const NotificationPermissionModal = ({ 
  isOpen, 
  onClose, 
  onEnable, 
  onSkip,
  theme = 'pink' 
}) => {
  if (!isOpen) return null;

  const isPink = theme === 'pink';
  const isWarm = theme === 'warm';

  const gradientStyle = isPink 
    ? '#e879a0'
    : isWarm 
      ? '#2D5A3D'
      : '#4361EE';

  const handleEnable = async () => {
    try {
      await onEnable();
    } catch (e) {
      console.error('Notification permission error:', e);
    }
    onClose();
  };

  const handleSkip = () => {
    try {
      onSkip?.();
    } catch (e) {
      console.error('Notification skip error:', e);
    }
    onClose();
  };

  return (
    <div data-testid="notification-permission-modal" className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={handleSkip}>
      <div className="bg-white rounded-3xl w-full max-w-xs overflow-hidden shadow-2xl animate-in fade-in zoom-in duration-200" onClick={(e) => e.stopPropagation()}>
        {/* Header with gradient */}
        <div 
          className="px-5 pt-5 pb-6 relative overflow-hidden"
          style={{ background: gradientStyle }}
        >
          {/* Decorative circles */}
          <div 
            className="absolute -right-8 -top-8 w-28 h-28 rounded-full"
            style={{ background: 'rgba(255,255,255,0.1)' }}
          />
          <div 
            className="absolute -left-4 -bottom-4 w-16 h-16 rounded-full"
            style={{ background: 'rgba(255,255,255,0.08)' }}
          />
          
          {/* Close button */}
          <button 
            onClick={handleSkip}
            className="absolute top-3 right-3 w-7 h-7 rounded-full bg-white/20 flex items-center justify-center text-white hover:bg-white/30 transition-colors"
          >
            <X className="w-3.5 h-3.5" />
          </button>
          
          {/* Bell icon */}
          <div className="relative z-10 flex justify-center">
            <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center">
              <Bell className="w-7 h-7 text-white" />
            </div>
          </div>
        </div>
        
        {/* Content */}
        <div className="px-5 pb-5 pt-3 text-center">
          <h2 
            className="text-lg font-bold text-gray-800 mb-1.5"
            style={{ fontFamily: "'DM Serif Display', serif" }}
          >
            Stay on Track
          </h2>
          <p className="text-gray-500 text-xs mb-4 leading-relaxed">
            Enable notifications for spending reminders, 
            budget updates, and financial milestones.
          </p>
          
          {/* Buttons */}
          <div className="space-y-2">
            <Button
              data-testid="notification-enable-btn"
              onClick={handleEnable}
              className="w-full py-2.5 rounded-xl font-semibold text-white text-sm"
              style={{ background: gradientStyle }}
            >
              Enable Notifications
            </Button>
            <button
              data-testid="notification-skip-btn"
              onClick={handleSkip}
              className="w-full py-2 text-gray-400 text-xs font-medium hover:text-gray-600 transition-colors"
            >
              Maybe Later
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationPermissionModal;
